<section class="omni-shell">
  <header class="omni-header">
    <div>
      <p class="eyebrow">Contact Center</p>
      <h1>Unified Inbox</h1>
      <p class="subtitle">CSR handoff queue for Facebook, WhatsApp, Instagram, YouTube, X, LinkedIn and future channels.</p>
    </div>
    <button class="btn btn-primary" type="button" (click)="refresh()" [disabled]="loading || saving">
      Refresh
    </button>
  </header>

  <div class="stat-grid">
    <div class="stat-card">
      <span>Pending</span>
      <strong>{{ stats.pending }}</strong>
    </div>
    <div class="stat-card">
      <span>Assigned</span>
      <strong>{{ stats.assigned }}</strong>
    </div>
    <div class="stat-card">
      <span>Reply queued</span>
      <strong>{{ stats.replied }}</strong>
    </div>
    <div class="stat-card">
      <span>Total</span>
      <strong>{{ stats.total }}</strong>
    </div>
  </div>

  <div class="filter-bar">
    <input [(ngModel)]="filterQ" placeholder="Search citizen, handle, phone or message..." (keyup.enter)="applyFilters()" />
    <select [(ngModel)]="filterSource" (change)="applyFilters()">
      <option value="">All sources</option>
      <option value="FACEBOOK">Facebook</option>
      <option value="WHATSAPP">WhatsApp</option>
      <option value="INSTAGRAM">Instagram</option>
      <option value="YOUTUBE">YouTube</option>
      <option value="X">X</option>
      <option value="LINKEDIN">LinkedIn</option>
      <option value="WEB_CHAT">Web Chat</option>
      <option value="SMS">SMS</option>
      <option value="EMAIL">Email</option>
      <option value="IVR">IVR</option>
    </select>
    <select [(ngModel)]="filterStatus" (change)="applyFilters()">
      <option value="">All statuses</option>
      <option value="PENDING_CSR">Pending</option>
      <option value="ASSIGNED">Assigned</option>
      <option value="CSR_REPLIED">Reply queued</option>
      <option value="CLOSED">Closed</option>
    </select>
    <button class="btn" type="button" (click)="applyFilters()">Apply</button>
    <button class="btn" type="button" (click)="clearFilters()">Clear</button>
  </div>

  <div class="notice notice-error" *ngIf="errorMessage">{{ errorMessage }}</div>
  <div class="notice notice-success" *ngIf="successMessage">{{ successMessage }}</div>

  <main class="inbox-layout">
    <aside class="thread-list">
      <div class="list-top">
        <strong>{{ totalElements }} conversations</strong>
        <span *ngIf="loading">Loading...</span>
      </div>

      <button
        *ngFor="let item of items"
        class="thread-item"
        [class.active]="selected?.id === item.id"
        type="button"
        (click)="select(item)">
        <div class="thread-row">
          <span class="source-badge" [ngClass]="'source-' + item.source.toLowerCase()">{{ sourceLabel(item.source) }}</span>
          <span class="time">{{ formatDate(item.createdAt) }}</span>
        </div>
        <strong>{{ item.citizenName || item.citizenHandle || item.citizenPhone || 'Unknown citizen' }}</strong>
        <p dir="auto">{{ item.messageText }}</p>
        <div class="thread-row">
          <span class="status-pill" [ngClass]="'status-' + item.status.toLowerCase()">{{ statusLabel(item.status) }}</span>
          <span class="delivery-pill" [ngClass]="'delivery-' + (item.deliveryStatus || 'none').toLowerCase()">
            {{ deliveryLabel(item.deliveryStatus, item) }}
          </span>
        </div>
      </button>

      <div class="empty" *ngIf="!loading && !items.length">No conversations match this filter.</div>

      <div class="pager" *ngIf="totalPages > 1">
        <button class="btn" type="button" (click)="goToPrev()" [disabled]="currentPage === 0">Prev</button>
        <span>{{ currentPage + 1 }} / {{ totalPages }}</span>
        <button class="btn" type="button" (click)="goToNext()" [disabled]="currentPage >= totalPages - 1">Next</button>
      </div>
    </aside>

    <section class="conversation-panel" *ngIf="selected; else noSelection">
      <header class="conversation-header">
        <div>
          <span class="source-badge" [ngClass]="'source-' + selected.source.toLowerCase()">{{ sourceLabel(selected.source) }}</span>
          <h2>{{ displayName }}</h2>
          <p>{{ selected.externalMessageId }}</p>
        </div>
        <div class="header-actions">
          <button class="btn" type="button" *ngIf="canAssign(selected)" (click)="assignToMe(selected)">
            Assign to me
          </button>
          <button class="btn" type="button" *ngIf="canClose(selected)" (click)="closeSelected()" [disabled]="saving">
            Close
          </button>
        </div>
      </header>

      <div class="message-card inbound">
        <span>Citizen message</span>
        <p dir="auto">{{ selected.messageText }}</p>
      </div>

      <div class="message-card bot" *ngIf="selected.botAnswer">
        <span>Bot attempt</span>
        <p dir="auto">{{ selected.botAnswer }}</p>
        <small>Confidence {{ confidence(selected.botConfidence) }} - {{ selected.handoffReason || 'Handoff' }}</small>
      </div>

      <div class="message-card human-reply" *ngIf="selected.replyText">
        <span>{{ selected.replyBy || 'CSR' }} reply</span>
        <p dir="auto">{{ selected.replyText }}</p>
        <small>
          {{ selected.repliedAt ? ('Queued ' + formatDate(selected.repliedAt)) : 'Reply saved' }}
          <ng-container *ngIf="selected.deliveryStatus"> - {{ deliveryLabel(selected.deliveryStatus, selected) }}</ng-container>
        </small>
      </div>

      <div class="meta-grid">
        <div>
          <span>Status</span>
          <strong>{{ statusLabel(selected.status) }}</strong>
        </div>
        <div>
          <span>Queue</span>
          <strong>{{ selected.assignedQueue || 'GENERAL' }}</strong>
        </div>
        <div>
          <span>Assigned agent</span>
          <strong>{{ selected.assignedAgentUsername || '-' }}</strong>
        </div>
        <div>
          <span>Delivery</span>
          <strong>
            <span class="delivery-pill" [ngClass]="'delivery-' + (selected.deliveryStatus || 'none').toLowerCase()">
              {{ deliveryLabel(selected.deliveryStatus, selected) }}
            </span>
          </strong>
        </div>
        <div>
          <span>Replied by</span>
          <strong>{{ selected.replyBy || '-' }}</strong>
        </div>
        <div>
          <span>Replied at</span>
          <strong>{{ formatDate(selected.repliedAt) }}</strong>
        </div>
        <div>
          <span>Delivered at</span>
          <strong>{{ formatDate(selected.deliveredAt) }}</strong>
        </div>
      </div>

      <div class="message-card error-card" *ngIf="selected.lastError">
        <span>Delivery error</span>
        <p>{{ selected.lastError }}</p>
      </div>

      <div class="reply-panel" *ngIf="canReply(selected); else replyUnavailable">
        <label for="replyText">CSR reply</label>
        <textarea id="replyText" [(ngModel)]="replyDraft" rows="6" placeholder="Write a clear reply for the citizen..."></textarea>
        <div class="reply-actions">
          <span class="error" *ngIf="selected.lastError">{{ selected.lastError }}</span>
          <button class="btn btn-primary" type="button" (click)="sendReply()" [disabled]="saving || !replyDraft.trim()">
            {{ replyButtonLabel(selected) }}
          </button>
        </div>
      </div>

      <ng-template #replyUnavailable>
        <div class="reply-panel reply-locked">
          <strong>{{ statusLabel(selected.status) }}</strong>
          <p *ngIf="selected.status === 'PENDING_CSR'">Assign this interaction before replying.</p>
          <p *ngIf="selected.status === 'CSR_REPLIED' && selected.deliveryStatus === 'PENDING'">
            Reply is queued and waiting for the channel adapter to deliver it.
          </p>
          <p *ngIf="selected.status === 'CLOSED'">This interaction is closed.</p>
        </div>
      </ng-template>
    </section>
  </main>
</section>

<ng-template #noSelection>
  <section class="conversation-panel empty-state">
    <h2>No conversation selected</h2>
    <p>Choose a pending item from the inbox to review and reply.</p>
  </section>
</ng-template>
